import { OAuth2 } from "@backstage/core-app-api";
import {
    ApiBlueprint,
    configApiRef,
    createApiFactory,
    createFrontendPlugin,
    discoveryApiRef,
    oauthRequestApiRef,
} from "@backstage/frontend-plugin-api";
import { jhidAuthApiRef } from "@internal/backstage-plugin-auth-jhid-react";

/**
 * API extension that registers the Jack Henry ID OAuth2 provider.
 *
 * This is NOT a Backstage sign-in provider — it is used for plugin-specific
 * authentication (e.g., AskJack, JH Now) where a JHID access token is needed
 * independently of the primary Okta sign-in flow.
 *
 * Default scopes are documentation only; actual scopes are determined by
 * `DEFAULT_SCOPES` in the backend module and `additionalScopes` in app-config.
 */
const jhidAuthApi = ApiBlueprint.make({
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: jhidAuthApiRef,
                deps: {
                    discoveryApi: discoveryApiRef,
                    oauthRequestApi: oauthRequestApiRef,
                    configApi: configApiRef,
                },
                factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
                    OAuth2.create({
                        configApi,
                        discoveryApi,
                        oauthRequestApi,
                        provider: {
                            id: "jhid",
                            title: "Jack Henry ID",
                            icon: () => null,
                        },
                        environment:
                            configApi.getOptionalString("auth.environment"),
                        defaultScopes: [
                            "openid",
                            "https://api.banno.com/enterprise/auth/offline_access",
                            "https://api.banno.com/consumer/auth/user.profile.readonly",
                        ],
                        popupOptions: {
                            size: {
                                fullscreen: false,
                            },
                        },
                    }),
            })
        ),
});

/**
 * The Jack Henry ID frontend plugin.
 *
 * Registers the JHID OAuth2 client for use by other plugins that need a
 * JHID access token (e.g. AskJack, JH Now, Firefighter). The API contract
 * (`jhidAuthApiRef`) and reusable React components/hooks live in the
 * `@internal/backstage-plugin-auth-jhid-react` web library; this plugin
 * package only provides the API extension.
 *
 * @public
 */
const authJhidPlugin = createFrontendPlugin({
    pluginId: "auth-jhid",
    extensions: [jhidAuthApi],
});

export default authJhidPlugin;
